import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { useQueryClient } from "@tanstack/react-query";

type OptimizedOrderFlag = {
  flagName: string;
  flagColor: string;
};

interface UnassignFlagProps {
  open: boolean;
  onClose: () => void;
  flag: OptimizedOrderFlag;
  orderId: number;
}

const UnassignFlag: React.FC<UnassignFlagProps> = ({
  open,
  onClose,
  flag,
  orderId,
}) => {
  const { translate, translateWithoutPrefix } =
    useTranslation("pages.orders.flag");
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const queryClient = useQueryClient();

  const invalidate = useInvalidate();
  const { mutate } = useCustomMutation();

  const handleConfirm = () => {
    mutate(
      {
        url: `store/order-flags/${orderId}`,
        method: "delete",
        values: {},
        successNotification: () => ({
          message: translate("successUnassignFlag"),
          type: "success",
        }),
      },
      {
        onSuccess: async () => {
          const queryKey = [
            "order-detail",
            selectedCountry?.id,
            String(orderId),
          ];

          invalidate({ resource: "all", invalidates: ["all"] });
          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });

          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <Stack direction="row" alignItems="center" mb={2}>
          <FlagIcon fontSize="large" sx={{ color: flag.flagColor, mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            {flag.flagName}
          </Typography>
        </Stack>
        <Typography>{translate("unassignFlag")}</Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <Button color="error" onClick={handleConfirm}>
          {translateWithoutPrefix("buttons.yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnassignFlag;
